import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { amiGet, Card, Kpi, Loading, NotYet, num, pct, tsFmt, isoFmt } from "@/lib/ami";

const day = (ms) => new Date(ms).toLocaleDateString("de-DE");
const REGIME = { Aufwaertstrend: "Aufwärtstrend", Abwaertstrend: "Abwärtstrend", Seitwaerts: "Seitwärts", Uebergang: "Übergang" };

function MetricsTable({ b }) {
  const rows = [["Gesamt", b.metrics], ["Entwicklung (In-Sample)", b.in_sample], ["Test (Out-of-Sample)", b.out_of_sample]];
  return (
    <div className="overflow-x-auto"><table className="w-full text-xs font-mono" data-testid="ami-bt-metrics">
      <thead><tr className="text-left uppercase tracking-wider text-muted-foreground font-sans"><th className="py-2">Zeitraum</th><th>Rendite</th><th>CAGR</th><th>Max DD</th><th>Sharpe</th><th>Trades</th><th>Trefferquote</th><th>Profit-Faktor</th><th>Ø Trade</th></tr></thead>
      <tbody>{rows.map(([l, m]) => <tr key={l} className="border-t border-border/60"><td className="py-1.5 font-sans">{l}</td><td>{pct(m.total_return_pct, 1)}</td><td>{pct(m.cagr_pct, 1)}</td><td>{pct(m.max_drawdown_pct, 1)}</td><td>{num(m.sharpe, 2)}</td><td>{m.trades}</td><td>{num(m.win_rate_pct, 1)} %</td><td>{num(m.profit_factor, 2)}</td><td>{pct(m.avg_trade_pct, 2)}</td></tr>)}</tbody>
    </table></div>
  );
}

function Detail({ id }) {
  const q = useQuery({ queryKey: ["ami-bt", id], queryFn: () => amiGet(`/backtests/${id}`) });
  if (q.isLoading) return <Loading />;
  const b = q.data;
  return (
    <div className="space-y-6" data-testid="ami-bt-detail">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Rendite (nach Kosten)" value={pct(b.metrics.total_return_pct, 1)} tone={b.metrics.total_return_pct >= 0 ? "pos" : "neg"} hint={`Buy & Hold ${pct(b.buy_hold_return_pct, 1)}`} testid="ami-bt-return" />
        <Kpi label="Max. Drawdown" value={pct(b.metrics.max_drawdown_pct, 1)} />
        <Kpi label="Sharpe" value={num(b.metrics.sharpe, 2)} hint={`${b.metrics.trades} Trades · Exposure ${num(b.metrics.exposure_pct, 0)} %`} />
        <Kpi label="Out-of-Sample Rendite" value={pct(b.out_of_sample.total_return_pct, 1)} hint={`ab ${day(b.split_ts)}`} />
      </div>
      <Card title={`${b.strategy_name} · ${b.inst} · ${b.bar}`} testid="ami-bt-equity">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={b.equity}>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="ts" tickFormatter={(t) => new Date(t).getFullYear()} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" minTickGap={40} />
            <YAxis scale="log" domain={["auto", "auto"]} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={50} tickFormatter={(v) => num(v, 1)} />
            <Tooltip labelFormatter={tsFmt} formatter={(v, n) => [num(v, 3), n]} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
            <Legend />
            <Line dataKey="eq" name="Strategie" stroke="hsl(var(--primary))" dot={false} strokeWidth={1.75} />
            <Line dataKey="bh" name="Buy & Hold" stroke="hsl(var(--muted-foreground))" dot={false} strokeWidth={1} />
          </LineChart>
        </ResponsiveContainer>
        <p className="mt-2 text-xs text-muted-foreground">Kosten: Gebühr {b.costs.fee_pct} % + Slippage {b.costs.slippage_pct} % je Seite, Funding {b.costs.funding_pct_8h} %/8 h (Annahme). Einstieg zum Open der Folgekerze; Stop und Take-Profit in derselben Kerze → Stop zählt zuerst.</p>
      </Card>
      <Card title="Kennzahlen" testid="ami-bt-metrics-card"><MetricsTable b={b} /></Card>
      <Card title="Nach Marktregime" testid="ami-bt-regime">
        {Object.entries(b.by_regime).map(([k, v]) => <div key={k} className="flex justify-between text-sm py-1.5 border-b border-border/50"><span>{REGIME[k] || k}</span><span className="font-mono text-xs">{v.trades} Trades · Ø {pct(v.avg_pct, 2)} · Treffer {num(v.win_rate_pct, 0)} %</span></div>)}
      </Card>
    </div>
  );
}

export default function AmiBacktesting() {
  const [sp, setSp] = useSearchParams();
  const id = sp.get("id");
  const q = useQuery({ queryKey: ["ami-bts"], queryFn: () => amiGet("/backtests") });
  return (
    <div className="space-y-6" data-testid="ami-backtesting">
      <Card title="Backtests" testid="ami-bt-list">
        {q.isLoading ? <Loading /> : q.data.length === 0 ? <NotYet text="Noch kein Backtest. Lege unter Strategieentwicklung eine Strategie an und starte dort einen Backtest." /> : (
          <div className="space-y-1">{q.data.map((b) => (
            <button key={b.id} onClick={() => setSp({ id: b.id })} data-testid={`ami-bt-${b.id}`} className={`w-full flex flex-wrap justify-between gap-2 text-left text-sm px-2 py-2 rounded ${id === b.id ? "bg-secondary" : "hover:bg-secondary/50"}`}>
              <span>{b.strategy_name} <span className="text-xs text-muted-foreground">{b.inst} · {b.bar}</span></span>
              <span className="font-mono text-xs">{pct(b.metrics.total_return_pct, 1)} · OOS {pct(b.out_of_sample.total_return_pct, 1)} · DD {pct(b.metrics.max_drawdown_pct, 1)} · {isoFmt(b.created_at)}</span>
            </button>
          ))}</div>
        )}
      </Card>
      {id && <Detail id={id} />}
    </div>
  );
}
